import * as angular from "angular";
import {Match} from "../models/match.model";
import {Recommendation} from "../models/recommendation.model";
import {NavianceSchoolWithProducts} from "../models/school/naviance-school-with-products.model";
import {CleverSchool} from "../models/school/clever-school.model";
import {MatchingService} from "../services/matching/matching.service";
import {ApprovedMatch} from "../models/approved-match.model";
import {NavianceSchool} from "../models/school/naviance-school.model";
import {AddressService} from "../services/address/address.service";
import * as moment from "moment-timezone";
import { LinkPathService } from "../link-path/link-path.service";
import { RecommendedAndMatchedSchool } from "../models/recommendedAndMatchedSchool.model";
import { FeatureFlagService } from "../services/launch-darkly/feature-flag.service";
import { FeatureFlagNames } from "../models/app-config.model";

const NAVIANCE_SCHOOLS_INIT_DATA = "navianceSchools";
const CLEVER_SCHOOLS_INIT_DATA = "cleverSchools";
const MATCHES_INIT_DATA = "matches";
const IS_NEW_EXPERIENCE = "isNewExperience";

export class MatchPreviewController {
    matches: Match[];
    navianceSchools: NavianceSchoolWithProducts[];
    cleverSchools: CleverSchool[];
    recommendations: Recommendation[];
    recommendedAndMatchedSchools: RecommendedAndMatchedSchool[];
    allMatchedSchools;
    toBeSavedMatches: ApprovedMatch[];
    recommendationsLoaded: boolean = false;
    editedMatch;
    toBeDeletedMatches: ApprovedMatch[] = [];
    filterText: string = "";
    isNewExperience: boolean;
    isSaveSuccessMessageVisible: boolean = false;
    recentlySavedMatchesCount: number;
    onMatchesUpdate: Function;
    isEditDrawerOpen: boolean = false;
    pilotStatus: boolean = false;
    saveWarningFlagVisible: boolean = false;
    notificationsEnabled=false;
    isStudentImportEnabled;
    isStaffImportEnabled;

    /* @ngInject */
    constructor(
        public $route: angular.route.IRouteService,
        private $location: angular.ILocationService,
        private $routeParams: { districtId: string },
        private matchingService: MatchingService,
        private addressService: AddressService,
        private $rootScope: angular.IRootScopeService,
        private linkPathService: LinkPathService,
        private featureFlagService: FeatureFlagService,
        private featureFlagNames: FeatureFlagNames
    ) {
        this.isNewExperience = this.$route.current.locals[IS_NEW_EXPERIENCE];
    }

    $onInit() {
        this.filterNonCleverNavianceSchools(this.$route.current.locals[NAVIANCE_SCHOOLS_INIT_DATA]);
        this.cleverSchools = this.$route.current.locals[CLEVER_SCHOOLS_INIT_DATA];
        this.matches = this.$route.current.locals[MATCHES_INIT_DATA];
        this.allMatchedSchools = this.listAllMatchedSchools();

        this.matchingService.getRecommendedMatches(this.$route.current.params.districtId).then(recommendations => {
            this.recommendations = recommendations;
            this.recommendedAndMatchedSchools = this.allMatchedSchools.concat(this.listAllRecommendations());
            this.recommendationsLoaded = true;
            this.toBeSavedMatches = this.getToBeSavedMatches();
        });

        this.recommendedAndMatchedSchools = this.allMatchedSchools.concat(this.listTemporaryRecommendations());
        if (this.onMatchesUpdate) {
            this.onMatchesUpdate(this.matches, this.recommendedAndMatchedSchools);
        }
        this.$rootScope.$on("clever-match:done", this.storeManualChangesForSaving);

        this.$rootScope.$on("drawer:close", (_event) => {
            this.isEditDrawerOpen = false;
        });

        this.featureFlagService().then((flags) => {
            let isStudentImportSystemEnabled = flags && flags.filter(
                (flag) => flag.name === this.featureFlagNames.STUDENT_SYSTEM_ENABLED
            )[0].value;
            if(isStudentImportSystemEnabled) {
                this.isStudentImportEnabled = !(flags && flags.filter(
                    (flag) => flag.name === this.featureFlagNames.STUDENT_TENANT_DISABLED
                )[0].value);
            } else {
                this.isStudentImportEnabled = flags && flags.filter(
                    (flag) => flag.name === this.featureFlagNames.STUDENT_TENANT_ENABLED
                )[0].value;
            }
            this.isStaffImportEnabled = !(flags && flags.filter(
                (flag) => flag.name === this.featureFlagNames.STAFF_TENANT_DISABLED
            )[0].value);
        }, () => {
            this.isStudentImportEnabled = true;
            this.isStaffImportEnabled = true;
        }); 
    }

    private updateInfo() {
        this.toBeSavedMatches.forEach((approvedMatch) => {
            this.recommendedAndMatchedSchools = this.recommendedAndMatchedSchools.map((school) => {
                if (school.navianceSchool.institutionUuid === approvedMatch.navianceSchool.institutionUuid) {
                    const match = {
                        navianceId: approvedMatch.navianceSchool.institutionUuid,
                        cleverSchoolId: approvedMatch.cleverSchool.cleverId,
                        updated: new Date().toISOString()
                    };

                    return {...school, match, status: "saved" };
                }

                return school;
            });
            this.matches = this.recommendedAndMatchedSchools.filter(school => school.match).map(school => school.match);
            this.onMatchesUpdate(this.matches, this.recommendedAndMatchedSchools);
        });
    }

    storeManualChangesForSaving = (_event, cleverSchool: CleverSchool) => {
        const index = this.recommendedAndMatchedSchools.indexOf(this.getEditedNavianceCleverSchool(this.editedMatch));
        const matchStatus = this.recommendedAndMatchedSchools[index].status;

        if (matchStatus === "saved") {
            this.toBeDeletedMatches.push(this.editedMatch);
        }

        this.notificationsEnabled=true;
        this.recommendedAndMatchedSchools[index].cleverSchool = cleverSchool;
        this.recommendedAndMatchedSchools[index].status = "recommended";
        this.toBeSavedMatches = this.getToBeSavedMatches();
        this.$rootScope.$emit("drawer:close", "select-clever-match");
    }

    getEditedNavianceCleverSchool(match) {
        if (!this.recommendedAndMatchedSchools || !match) { return; }

        return this.recommendedAndMatchedSchools.filter(rec => rec.navianceSchool === match.navianceSchool)[0];
    }

    getTotalMatched(): number {
        if (!this.recommendedAndMatchedSchools) { return; }
        
        return this.recommendedAndMatchedSchools.filter(rec => {
            return rec.cleverSchool !== undefined && rec.status === "recommended";
        }).length;
    }

    getTotalMatchedAndSaved(): number {
        if (!this.recommendedAndMatchedSchools) { return; }
        
        return this.recommendedAndMatchedSchools.filter(matched => {
            return matched.cleverSchool !== undefined && matched.status === "recommended" || matched.status === "saved";
        }).length;
    }

    getTotalSaved(): number {
        if (!this.recommendedAndMatchedSchools) { return; }
        
        return this.recommendedAndMatchedSchools.filter(rec => {
            return rec.cleverSchool !== undefined && rec.status === "saved";
        }).length;
    }

    hasSavedMatch(): boolean {
        return this.getTotalSaved() > 0;
    }

    getTotalImports(): number {
        if (!this.recommendedAndMatchedSchools) { return; }

        return this.recommendedAndMatchedSchools.filter(rec => {
            return this.isSchoolImporting(rec) || this.isSchoolImported(rec);
        }).length;
    }

    getTotalUnmatched(): number {
        if (!this.recommendationsLoaded && !this.recommendedAndMatchedSchools) { return; }

        return this.recommendedAndMatchedSchools.filter(rec =>
            rec.cleverSchool === undefined && rec.status === "recommended").length;
    }

    private getLengthIfHaveRecommendedMatches(haveRecMatches: boolean): number {
        if (!this.recommendations) { return; }

        return haveRecMatches ? this.recommendations.filter(rec => rec.cleverSchoolId && !this.getSavedMatch(rec.navianceId)).length :
            this.recommendations.filter(rec => !rec.cleverSchoolId).length;
    }

    filterNonCleverNavianceSchools(navianceSchools: NavianceSchoolWithProducts[]) {
        this.navianceSchools = navianceSchools;
    }

    private getNavianceSchool(navianceId): NavianceSchool {
        const navianceSchool = this.navianceSchools.filter(naviance => {
            return navianceId === naviance.navianceSchool.institutionUuid;
        })[0];
        if (navianceSchool) {
            return navianceSchool.navianceSchool;
        }
    }

    private getCleverSchool(cleverId): CleverSchool {
        return this.cleverSchools.filter(clever => cleverId === clever.cleverId)[0];
    }

    private getSavedMatch(navianceId): Match {
        return this.matches.filter(match => navianceId === match.navianceId)[0];
    }

    isSavedMatchAvailable() {
        if (!this.matches) { return false; }

        return this.matches.length > 0;
    }

    listAllMatchedSchools() {
        if (this.matches && this.navianceSchools && this.cleverSchools) {
            return this.matches.map(match => {

                const navianceSchool = this.getNavianceSchool(match.navianceId);

                const cleverSchool = this.getCleverSchool(match.cleverSchoolId);

                if (navianceSchool) {
                    return {navianceSchool, cleverSchool, match, status: "saved"};
                }
            }).filter(match => match);
        }
        return [];
    }

    listAllRecommendations() {
        if (this.recommendations && this.navianceSchools && this.cleverSchools) {
            return this.recommendations.map(recommendation => {

                const navianceSchool = this.getNavianceSchool(recommendation.navianceId);

                let cleverSchool = this.getCleverSchool(recommendation.cleverSchoolId);

                const savedMatch = this.getSavedMatch(recommendation.navianceId);

                cleverSchool = !cleverSchool ? void 0 : cleverSchool;
                if (navianceSchool && !savedMatch) {
                    return {navianceSchool, cleverSchool, savedMatch, status: "recommended"};
                }
            }).filter(recommendation => recommendation);
        }
        return [];
    }

    listTemporaryRecommendations() {
        if (!this.navianceSchools || !this.cleverSchools) { return []; }
        return this.navianceSchools.reduce((tempRecs, tempMatch) => {
            
            const hasMatch = this.matches.some(match => tempMatch.navianceSchool.institutionUuid === match.navianceId);

            if (hasMatch) { return tempRecs; }

            tempRecs.push({
                "navianceSchool": this.getNavianceSchool(tempMatch.navianceSchool.institutionUuid),
                "cleverSchool": void 0,
                "status": "loading"
            });

            return tempRecs;
        }, []);
    }

    getCleverUnmatchedSchools() {
        if (this.cleverSchools && this.recommendations && this.matches) {
            return this.cleverSchools.map(cleverSchool => {
                const match = this.recommendedAndMatchedSchools.filter(recommendedAndMatchedSchool => {
                    return recommendedAndMatchedSchool.cleverSchool === cleverSchool;
                })[0];
                if (!match) {
                    return cleverSchool;
                }
            }).filter(cleverSchool => cleverSchool);
        }

        return [];
    }

    cleverSchoolExistsInRecommendation(rec): boolean {
        return rec.cleverSchool ? true : false;
    }

    schoolNotSharedFromCleverButFromNaviance(rec:RecommendedAndMatchedSchool): boolean {
        return (
            !rec.cleverSchool
            && rec.match
            && rec.navianceSchool
            && rec.match.cleverSchoolId != null
            && (rec.match.navianceId ===  rec.navianceSchool.institutionUuid)
        );
    }

    cleverSchoolExistsOrLoadedInRecommendation(rec): boolean {
        return this.cleverSchoolExistsInRecommendation(rec) || !this.recommendationsLoaded;
    }

    isSchoolRecommended(status) {
        return status === "recommended";
    }

    getSavedMatchButtonName() {
        return this.getTotalMatched() === 1 ? "Match" : "Matches";
    }

    getCardButtonName(rec:RecommendedAndMatchedSchool): string {
        if (this.schoolNotSharedFromCleverButFromNaviance(rec) && rec.status === "saved") {
            return `Match saved ${(moment as any).tz(rec.match.updated, "UTC").local().format("MMM DD YYYY hh:mm A")}`;
        } else if (rec.match && rec.match.status === "IMPORT_COMPLETE" && rec.match.staffStatus === "IMPORT_COMPLETE") {
            return "Imported: Students & Staff";
        } else if (rec.match && rec.match.status === "IMPORT_COMPLETE") {
            return "Imported: Students";
        } else if (this.isSchoolImporting(rec)) {
            return "Import in progress...";
        } else if (rec.match && rec.match.staffStatus === "IMPORT_COMPLETE") {
            return "Imported: Staff";
        } else if (rec.cleverSchool && rec.status === "saved") {
            return `Match saved ${(moment as any).tz(rec.match.updated, "UTC").local().format("MMM DD YYYY hh:mm A")}`;
        } else if (rec.cleverSchool && rec.status === "recommended") {
            return "Matched (not saved)";
        } else if (!this.recommendationsLoaded || rec.status === "loading") {
            return "Loading";
        }
        return "Unmatched";
    }

    activeFilterButton(status) { return this.filterText === status; }

    cleverFilter = (rec) => {
        if ("" === this.filterText) { return true;}
        if (this.getCardButtonName(rec) === this.filterText) { return true;}
        if (this.filterText === "Imported" && (this.isSchoolImported(rec) || this.isSchoolImporting(rec))) {
            return true;
        }
        if (this.filterText === "Matched" && this.isNotImportStatus(rec)) { return true;}
        return false;
    }

    getToBeSavedMatches(): ApprovedMatch[] {
        if (!this.recommendedAndMatchedSchools) { return []; }

        return this.recommendedAndMatchedSchools.map(recommendation => {
            if (recommendation.navianceSchool.institutionUuid &&
                recommendation.cleverSchool && recommendation.status === "recommended") {
                return {
                    navianceSchool: this.getNavianceSchool(recommendation.navianceSchool.institutionUuid),
                    cleverSchool: this.getCleverSchool(recommendation.cleverSchool.cleverId)
                };
            }
        }).filter(recommendation => recommendation);
    }

    closeWithoutSavingMatches() {
        return this.$location.path(this.$routeParams.districtId);
    }

    saveMatchedSchools(): void {
        this.deleteSavedMatch();
        this.toBeSavedMatches = this.getToBeSavedMatches();

        if (this.toBeSavedMatches.length > 0) {
            this.matchingService.approveMatches(this.$routeParams.districtId, this.toBeSavedMatches)
                .then(() => {
                    if (this.isNewExperience) {
                        this.isSaveSuccessMessageVisible = true;
                        this.recentlySavedMatchesCount = this.toBeSavedMatches.length;
                        this.updateInfo();
                    } else {
                        this.$location.path(this.$routeParams.districtId);
                    }
                });
        }
        this.updateInfo();
        this.toBeSavedMatches = [];
        this.toBeDeletedMatches = [];
    }

    deleteSavedMatch() {
        if (!this.toBeDeletedMatches) { return; }
        this.isSaveSuccessMessageVisible = true;
        return this.toBeDeletedMatches.forEach(deleteMatch => {
            this.matchingService.unmatchApprovedMatch(this.$routeParams.districtId, deleteMatch);
        });
    }

    selectCleverMatch(navianceSchool: NavianceSchoolWithProducts) {
        this.editedMatch = {navianceSchool: navianceSchool};
        this.$rootScope.$emit("clever-match:schools", navianceSchool, this.getCleverUnmatchedSchools());
        this.isEditDrawerOpen = true;
    }

    editCleverMatch(navianceSchool: NavianceSchool, cleverSchool: CleverSchool) {
        this.editedMatch = {navianceSchool: navianceSchool, cleverSchool: cleverSchool};
        this.$rootScope.$emit("clever-match:edit", navianceSchool, cleverSchool, this.getCleverUnmatchedSchools());
        this.isEditDrawerOpen = true;
    }

    isCleverRecommendedOrSavedMatchChanged(): boolean {
        if (!this.recommendations) { return false; }

        return this.getTotalMatched() > this.getLengthIfHaveRecommendedMatches(true) ||
            this.toBeDeletedMatches.length > 0;
    }

    hasGreekingStatus(recommendation) {
        return !this.recommendationsLoaded && recommendation.cleverSchool == null;
    }

    getNavianceSchoolAddressLine(navianceSchool: NavianceSchool): string {
        return this.addressService.getNavianceSchoolAddressLine(navianceSchool);
    }

    getNavianceSchoolCityStateZip(navianceSchool: NavianceSchool): string {
        return this.addressService.getNavianceSchoolCityStateZip(navianceSchool);
    }

    getCleverSchoolAddress(cleverSchool: CleverSchool): string {
        return this.addressService.getCleverSchoolAddress(cleverSchool);
    }

    getCleverSchoolCityStateZip(cleverSchool: CleverSchool): string {
        return this.addressService.getCleverSchoolCityStateZip(cleverSchool);
    }

    isSchoolImported(rec): boolean {
        return rec.match && (rec.match.status === "IMPORT_COMPLETE" || rec.match.staffStatus === "IMPORT_COMPLETE");
    }

    isSchoolImporting(rec): boolean {
        return rec.match && (rec.match.status === "IMPORT_IN_PROGRESS" || rec.match.staffStatus === "IMPORT_IN_PROGRESS");
    }

    isNotImportStatus(rec): boolean {
        return this.cleverSchoolExistsInRecommendation(rec) && ! (this.isSchoolImporting(rec) || this.isSchoolImported(rec));
    }

    isCleverSchoolExistsInRecommendation(rec): boolean {
        return this.cleverSchoolExistsInRecommendation(rec) ;
    }

    isPageThreeReady() {
        if (!this.matches) { return false; }
        return this.matches.filter((match) => {
                return match.status === "TEST_MATCH_DONE"
                    || match.status === "IMPORT_IN_PROGRESS"
                    || match.status === "IMPORT_COMPLETE";
            }).length > 0;
    }

    goToStudentImport() {
        if (this.isSafeToNavigate()) {
            return this.$location.path(`${this.$routeParams.districtId}/${this.linkPathService.getStudentTabPath()}`);
        }
        this.updateWarningVisibility();
    }

    goToStaffImport() {
        if (this.isSafeToNavigate()) {
            return this.$location.path(`${this.$routeParams.districtId}/${this.linkPathService.getStaffTabPath()}`);
        }
        this.updateWarningVisibility();
    }

    /* istanbul ignore next */
    checkIfAMatchedSchoolIsUnmatched = (isSchoolUnmatch: boolean): void => {
        if (this.isNewExperience && isSchoolUnmatch) {
            this.isSaveSuccessMessageVisible = false;
        }
    }

    isVisible(): boolean {
        return this.pilotStatus;
    }

    saveButtonEnabled(): boolean {
        return (this.toBeSavedMatches && this.toBeSavedMatches.length !== 0) 
        || (this.toBeDeletedMatches && this.toBeDeletedMatches.length !== 0);
    }

    isSafeToNavigate(): boolean {
        return !this.saveButtonEnabled() || this.saveWarningFlagVisible;
    }
    
    updateWarningVisibility(): void {
        this.saveWarningFlagVisible = true;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/match-preview/match-preview.controller.ts